import { Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { AdminUserStatus, UserAccountRoleType } from '@/client/types/admin/users-orgs';
import { roleTypeLabel } from './helpers';
import { MultiSelectFilter } from '@/client/components/admin/MultiSelectFilter';
import { CREATED_PRESETS, USER_STATUSES } from './use-user-list-state';

const selectClass =
  'rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export const UsersFilters: React.FC<{
  search: string;
  onSearch: (v: string) => void;
  searchBusy: boolean;
  roleTypes: UserAccountRoleType[];
  roleIds: number[];
  onRoles: (ids: number[]) => void;
  statuses: AdminUserStatus[];
  onStatuses: (s: AdminUserStatus[]) => void;
  email: string;
  onEmail: (v: string) => void;
  mfa: string;
  onMfa: (v: string) => void;
  created: string;
  createdFrom: string;
  createdTo: string;
  onCreated: (created: string, from?: string, to?: string) => void;
  hasFilters: boolean;
  onClearAll: () => void;
}> = (p) => (
  <div className="flex flex-wrap items-end gap-4">
    <div className="space-y-1">
      <label htmlFor="filter-search" className="block text-xs font-medium text-gray-500">
        Search
      </label>
      <div className="relative w-64">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <Input
          id="filter-search"
          className="pl-9 pr-9"
          placeholder="Name, email or phone…"
          value={p.search}
          onChange={(e) => p.onSearch(e.target.value)}
        />
        {p.searchBusy && (
          <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400" aria-label="Loading" />
        )}
      </div>
    </div>

    <MultiSelectFilter
      label="Role"
      options={p.roleTypes.map((rt) => ({ key: rt.uartid, label: roleTypeLabel(rt) }))}
      value={p.roleIds}
      onChange={p.onRoles}
    />

    <MultiSelectFilter label="Status" options={USER_STATUSES} value={p.statuses} onChange={p.onStatuses} />

    <div className="space-y-1">
      <label htmlFor="filter-email" className="block text-xs font-medium text-gray-500">
        Email
      </label>
      <select id="filter-email" className={selectClass} value={p.email} onChange={(e) => p.onEmail(e.target.value)}>
        <option value="any">Any</option>
        <option value="verified">Verified</option>
        <option value="unverified">Unverified</option>
      </select>
    </div>

    <div className="space-y-1">
      <label htmlFor="filter-mfa" className="block text-xs font-medium text-gray-500">
        Two-step
      </label>
      <select id="filter-mfa" className={selectClass} value={p.mfa} onChange={(e) => p.onMfa(e.target.value)}>
        <option value="any">Any</option>
        <option value="on">On</option>
        <option value="off">Off</option>
      </select>
    </div>

    <div className="space-y-1">
      <label htmlFor="filter-created" className="block text-xs font-medium text-gray-500">
        Created
      </label>
      <select
        id="filter-created"
        className={selectClass}
        value={p.created}
        onChange={(e) => p.onCreated(e.target.value, p.createdFrom, p.createdTo)}
      >
        <option value="">Any time</option>
        {CREATED_PRESETS.map((preset) => (
          <option key={preset.key} value={preset.key}>
            {preset.label}
          </option>
        ))}
        <option value="custom">Custom range…</option>
      </select>
    </div>

    {p.created === 'custom' && (
      <div className="flex items-end gap-2">
        <div className="space-y-1">
          <label htmlFor="filter-created-from" className="block text-xs font-medium text-gray-500">
            From
          </label>
          <Input
            id="filter-created-from"
            type="date"
            className="w-40"
            value={p.createdFrom}
            max={p.createdTo || undefined}
            onChange={(e) => p.onCreated('custom', e.target.value, p.createdTo)}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="filter-created-to" className="block text-xs font-medium text-gray-500">
            To
          </label>
          <Input
            id="filter-created-to"
            type="date"
            className="w-40"
            value={p.createdTo}
            min={p.createdFrom || undefined}
            onChange={(e) => p.onCreated('custom', p.createdFrom, e.target.value)}
          />
        </div>
      </div>
    )}

    {p.hasFilters && (
      <Button variant="ghost" className="text-gray-600" onClick={p.onClearAll}>
        Clear all filters
      </Button>
    )}
  </div>
);
